import { useEffect, useMemo, useState } from "react";
import UiIcon from "./UiIcon";

const STATUS_LABELS = {
  preparation: "آماده‌سازی",
  draft: "آماده‌سازی",
  active: "فعال",
  closing: "جمع‌بندی",
  closed: "قفل‌شده",
  locked: "قفل‌شده",
  archived: "آرشیوشده",
};

const MODULE_LABELS = {
  routine: "وظایف مستمر",
  activity: "فعالیت‌های غیرمستمر",
  peer: "بازخورد همکاران",
  indicator: "شاخص‌ها",
  period: "دوره ارزیابی",
};

export function PeriodContextBar({ period, daysLeft = null }) {
  if (!period) {
    return (
      <div className="cvi-period-context is-empty">
        <UiIcon name="planning" size={15} />
        <span>دوره فعالی وجود ندارد</span>
      </div>
    );
  }
  const statusKey = String(period.status || (period.isActive ? "active" : "")).toLowerCase();
  const status = STATUS_LABELS[statusKey] || (period.isActive ? "فعال" : "در حال تنظیم");
  const remaining = daysLeft == null ? "" : daysLeft > 0 ? `${daysLeft} روز تا پایان` : daysLeft === 0 ? "امروز آخرین روز" : "دوره پایان یافته";
  return (
    <div className="cvi-period-context" role="status" aria-label="وضعیت دوره ارزیابی">
      <span className={`cvi-period-context-status status-${statusKey || "default"}`}><i />{status}</span>
      <strong>{period.label || period.name || "دوره جاری"}</strong>
      {remaining && <span className="cvi-period-context-remaining">{remaining}</span>}
    </div>
  );
}

export function DetailDrawer({ open, title, eyebrow = "جزئیات", description, onClose, children, footer }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = event => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="cvi-drawer-layer" role="presentation">
      <button type="button" className="cvi-drawer-backdrop" aria-label="بستن" onClick={onClose} />
      <aside className="cvi-detail-drawer" role="dialog" aria-modal="true" aria-label={title}>
        <header className="cvi-detail-drawer-head">
          <div>
            {eyebrow && <span>{eyebrow}</span>}
            <h3>{title}</h3>
            {description && <p>{description}</p>}
          </div>
          <button type="button" className="cvi-detail-drawer-close" onClick={onClose} aria-label="بستن">
            <UiIcon name="close" size={17} />
          </button>
        </header>
        <div className="cvi-detail-drawer-body">{children}</div>
        {footer && <footer className="cvi-detail-drawer-footer">{footer}</footer>}
      </aside>
    </div>
  );
}

function localizedTaskTitle(task) {
  const type = String(task?.type || "").toUpperCase();
  if (type === "SELF_ASSESSMENT") {
    const month = String(task?.id || "").split("-").pop();
    return `تکمیل خوداظهاری${month ? ` · ماه ${month}` : ""}`;
  }
  if (type === "ROUTINE_EVALUATION") return `ارزیابی وظایف مستمر${task?.subjectName ? ` · ${task.subjectName}` : ""}`;
  if (type === "PEER_FEEDBACK") return `ثبت بازخورد همکاران${task?.subjectName ? ` · ${task.subjectName}` : ""}`;
  if (type === "INDICATOR_APPROVAL") return `بررسی شاخص · ${task?.title || "شاخص"}`;
  if (type === "INDICATOR_ENTRY") return `ثبت عملکرد شاخص · ${task?.title || "شاخص"}`;
  if (type === "ACTIVITY_REVIEW") return `بررسی فعالیت · ${task?.title || "فعالیت"}`;
  if (type === "ACTIVITY_REVISION") return `اصلاح فعالیت · ${task?.title || "فعالیت"}`;
  if (type === "ACTIVITY_DRAFT") return `تکمیل فعالیت · ${task?.title || "فعالیت"}`;
  return task?.title || "اقدام جدید";
}

function dueLabel(task) {
  if (task?.overdue) return "سررسید گذشته";
  if (!task?.dueAt) return "";
  const due = new Date(task.dueAt);
  if (Number.isNaN(due.getTime())) return "";
  const diff = Math.ceil((due.getTime() - Date.now()) / 86400000);
  if (diff < 0) return "سررسید گذشته";
  if (diff === 0) return "سررسید امروز";
  if (diff === 1) return "۱ روز مانده";
  return `${diff} روز مانده`;
}

export function DashboardActionCenter({ apiBaseUrl, activePeriod, onAction, maxItems = 4 }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const token = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("cvi_token") : null;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!token || !activePeriod?.id) {
        if (!cancelled) { setTasks([]); setLoading(false); }
        return;
      }
      setTasks([]);
      setLoading(true);
      setError("");
      try {
        const response = await fetch(`${apiBaseUrl}/api/workflow/tasks?periodId=${encodeURIComponent(activePeriod.id)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.message || "workflow load failed");
        if (!cancelled) setTasks(payload.tasks || []);
      } catch (loadError) {
        console.error("Dashboard action center load failed:", loadError);
        if (!cancelled) setError("اقدامات جاری دریافت نشد.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [apiBaseUrl, activePeriod?.id, token]);

  const visible = useMemo(() => tasks.slice(0, maxItems), [tasks, maxItems]);
  if (!activePeriod) return null;

  return (
    <section className="cvi-action-center" aria-label="اقدامات نیازمند توجه">
      <div className="cvi-action-center-head">
        <div>
          <span>اقدام بعدی</span>
          <h2>کارهای نیازمند توجه شما</h2>
        </div>
        {!loading && !error && <strong>{tasks.length ? `${tasks.length} مورد` : "همه‌چیز به‌روز است"}</strong>}
      </div>

      {loading && <div className="cvi-action-center-state">در حال دریافت اقدامات...</div>}
      {!loading && error && <div className="cvi-action-center-state is-error">{error}</div>}
      {!loading && !error && tasks.length === 0 && (
        <div className="cvi-action-center-empty">
          <span><UiIcon name="check" size={17} /></span>
          <div><strong>اقدام معوقی ندارید</strong><small>در حال حاضر موردی برای پیگیری فوری وجود ندارد.</small></div>
        </div>
      )}
      {!loading && !error && tasks.length > 0 && (
        <div className="cvi-action-list">
          {visible.map(task => {
            const due = dueLabel(task);
            return (
              <button key={task.id} type="button" className={`cvi-action-item${task.overdue ? " is-overdue" : ""}`} onClick={() => onAction?.(task)}>
                <span className="cvi-action-item-icon"><UiIcon name={task.module === "indicator" ? "target" : task.module === "peer" ? "peer" : task.module === "routine" ? "routine" : "activity"} size={16} /></span>
                <span className="cvi-action-item-copy">
                  <b>{localizedTaskTitle(task)}</b>
                  <small>{MODULE_LABELS[task.module] || "اقدام"}{task.subjectName ? ` · ${task.subjectName}` : ""}</small>
                </span>
                {due && <em>{due}</em>}
                <UiIcon name="chevronLeft" size={15} />
              </button>
            );
          })}
          {tasks.length > visible.length && <div className="cvi-action-more">+{tasks.length - visible.length} اقدام دیگر در مرکز کارها</div>}
        </div>
      )}
    </section>
  );
}
